import { useState } from 'react';
import { Info } from 'lucide-react';
import { PacketVisibility, type Packet } from '@promo/shared';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PacketVisibilityControlProps {
  packet: Pick<Packet, 'visibility'>;
  canManage: boolean;
  isUpdating?: boolean;
  onVisibilityChange: (visibility: PacketVisibility) => Promise<unknown> | void;
}

export function PacketVisibilityControl({
  packet,
  canManage,
  isUpdating = false,
  onVisibilityChange,
}: PacketVisibilityControlProps) {
  const [showStandardConfirm, setShowStandardConfirm] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const isRestricted = packet.visibility === PacketVisibility.RESTRICTED;
  const disabled = !canManage || isUpdating || isConfirming;

  const handleCheckedChange = (checked: boolean) => {
    if (checked) {
      void onVisibilityChange(PacketVisibility.RESTRICTED);
      return;
    }

    setShowStandardConfirm(true);
  };

  const confirmStandardVisibility = async () => {
    setIsConfirming(true);
    try {
      await onVisibilityChange(PacketVisibility.STANDARD);
      setShowStandardConfirm(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-md px-4 py-3">
        <Switch
          id="packet-restricted-visibility"
          checked={isRestricted}
          onCheckedChange={handleCheckedChange}
          disabled={disabled}
          aria-label="Restricted visibility"
        />
        <label
          htmlFor="packet-restricted-visibility"
          className="text-sm font-medium text-foreground"
        >
          Restricted visibility
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Restricted visibility details"
              >
                <Info className="h-4 w-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="left"
              align="center"
              sideOffset={12}
              collisionPadding={16}
              className="w-48 max-w-[calc(100vw-2rem)] border-0 bg-foreground px-4 py-3 text-left text-sm leading-6 text-background shadow-xl md:w-[22rem]"
            >
              Hidden from broad senior-leader access. HR and packet-scoped participants can
              still view the packet.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <AlertDialog open={showStandardConfirm} onOpenChange={setShowStandardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change to standard visibility?</AlertDialogTitle>
            <AlertDialogDescription>
              Standard visibility makes this packet available to senior leaders with global
              packet access. Packet-scoped participants and HR can already view it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isConfirming}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void confirmStandardVisibility();
              }}
              disabled={isConfirming}
            >
              Change to standard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
